import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Menu } from "lucide-react";

import { HubModuleNav } from "@/components/HubModuleNav";
import type { HubMockupDocument } from "@/lib/hubMockupApi";
import { apiFetch } from "@/lib/api";
import { moduleNavBarItems, type NavItem } from "@/lib/appNavigation";
import { isFloorTabletDevice } from "@/lib/floorTabletDevice";
import { cn } from "@/lib/utils";

export type ModuleNavGroup = {
  id: string;
  label: string;
  icon: string;
  pagePaths: string[];
};

export function moduleNavQueryKey(moduleKey: string): readonly [string, string] {
  return ["module-nav", moduleKey];
}

export async function fetchModuleNavGroups(moduleKey: string): Promise<ModuleNavGroup[]> {
  const body = await apiFetch<{ groups: ModuleNavGroup[] }>(`/api/module-nav/${moduleKey}`);
  return body.groups ?? [];
}

function isSettingsItem(item: NavItem): boolean {
  return item.to.endsWith("/settings") || item.label.trim().toLowerCase() === "settings";
}

function groupForPath(groups: ModuleNavGroup[], pathname: string): ModuleNavGroup | null {
  let match: ModuleNavGroup | null = null;
  for (const group of groups) {
    if (group.pagePaths.some((pagePath) => pathname === pagePath || pathname.startsWith(`${pagePath}/`))) {
      match = group;
    }
  }
  return match;
}

export function ModuleGroupedNav({
  app,
  navItems,
  pathname,
  hubNavDocument,
  onNavigate,
}: {
  app: string;
  navItems: NavItem[];
  pathname: string;
  hubNavDocument: HubMockupDocument | null | undefined;
  onNavigate: () => void;
}) {
  const groupsQuery = useQuery({
    queryKey: moduleNavQueryKey(app),
    queryFn: () => fetchModuleNavGroups(app),
    staleTime: 60_000,
  });
  const groups = groupsQuery.data ?? [];
  const [tablet, setTablet] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => {
    const read = (): void => setTablet(isFloorTabletDevice());
    read();
    window.addEventListener("resize", read);
    return () => window.removeEventListener("resize", read);
  }, []);

  if (groups.length === 0) {
    return (
      <HubModuleNav
        app={app}
        navItems={navItems}
        pathname={pathname}
        hubNavDocument={hubNavDocument}
        onNavigate={onNavigate}
      />
    );
  }

  const pages = moduleNavBarItems(navItems).filter((item) => !isSettingsItem(item));
  const assigned = new Set(groups.flatMap((group) => group.pagePaths));
  const ungrouped = pages.filter((item) => !assigned.has(item.to));
  const visibleGroups: ModuleNavGroup[] = [
    ...groups,
    ...(ungrouped.length > 0
      ? [{ id: "ungrouped", label: "Ungrouped", icon: "boxes", pagePaths: ungrouped.map((item) => item.to) }]
      : []),
  ];
  const current = groupForPath(visibleGroups, pathname) ?? visibleGroups[0];
  const currentPages = pages.filter((item) => current.pagePaths.includes(item.to));

  if (!tablet) {
    return (
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-1">
          {visibleGroups.map((group) => (
            <Link
              key={group.id}
              to={pages.find((item) => group.pagePaths.includes(item.to))?.to ?? pathname}
              onClick={onNavigate}
              className={cn(
                "rounded-md px-2 py-1 text-xs font-semibold",
                group.id === current.id ? "bg-brand-700 text-white" : "text-slate-700 hover:bg-slate-100",
              )}
            >
              {group.label}
            </Link>
          ))}
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-1">
          {currentPages.map((item) => {
            const selected = pathname === item.to || pathname.startsWith(`${item.to}/`);
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={onNavigate}
                className={cn(
                  "rounded-full px-2.5 py-1 text-sm",
                  selected ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100",
                )}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <button
        type="button"
        aria-label="Open groups"
        onClick={() => setDrawerOpen(true)}
        className="inline-flex h-12 w-12 items-center justify-center rounded-md border border-slate-300 bg-white"
      >
        <Menu className="h-5 w-5" />
      </button>
      <div className="flex flex-wrap gap-2">
        {currentPages.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className="inline-flex h-12 items-center rounded-full border border-slate-300 bg-white px-4 text-base font-medium"
          >
            {item.label}
          </Link>
        ))}
      </div>
      {drawerOpen ? (
        <div className="fixed inset-0 z-40 flex">
          <button type="button" aria-label="Close groups" className="absolute inset-0 bg-slate-900/40" onClick={() => setDrawerOpen(false)} />
          <div className="relative flex h-full w-72 flex-col bg-white p-3 shadow-xl">
            {visibleGroups.map((group) => (
              <Link
                key={group.id}
                to={pages.find((item) => group.pagePaths.includes(item.to))?.to ?? pathname}
                onClick={() => {
                  setDrawerOpen(false);
                  onNavigate();
                }}
                className="flex h-12 items-center rounded-md px-3 text-base font-semibold hover:bg-slate-100"
              >
                {group.label}
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
